/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import * as AppStrings from '@touchplan/strings';
import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ImageSize } from 'src/app/app-data/state/AbstractFileAttachApi';
import { Attachment } from 'src/app/app-data/models/Attachment';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { AttachedFilesService } from '../../attached-files.service';
import { ErrorStateMatcher } from '@angular/material/core';
import fileUploadValidator from 'src/app/app-data/validators/file-upload-validators.json';

@Component({
	selector: 'selected-image-view',
	templateUrl: './selected-image-view.component.html',
	styleUrls: ['./selected-image-view.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectedImageViewComponent implements OnInit, AfterViewInit, OnDestroy {
	private readonly CHAR_LIMIT = fileUploadValidator.imageAttach.fileDescription.charLimit;

	public Strings = AppStrings;
	public imageSize = ImageSize;
	public fileDescription: UntypedFormControl = new UntypedFormControl(
		'',
		Validators.maxLength(this.CHAR_LIMIT)
	);
	public errorStateMatcher: ErrorStateMatcher;
	public errorMessage = AppStrings.MAX_CHAR_ERROR_MESSAGE;
	public carouselSlideWidth: number;

	private destroy$: Subject<void>;
	private readonly CAROUSEL_SLIDE_WIDTH = 300;

	constructor(public attachedFilesService: AttachedFilesService) {
		this.destroy$ = new Subject();
		this.attachedFilesService.fileSelected$
			.pipe(
				filter((file: Attachment) => !!file),
				takeUntil(this.destroy$)
			)
			.subscribe((file: Attachment) => {
				this.fileDescription.patchValue(file.view.description);
			});
	}

	public ngOnInit(): void {
		this.attachedFilesService.updatePreviewFileUrls();

		this.carouselSlideWidth = this.CAROUSEL_SLIDE_WIDTH;

		this.fileDescription.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((description: string) => {
			if (description?.length <= this.CHAR_LIMIT) {
				this.attachedFilesService.api.localUpdateAttachment(
					this.attachedFilesService.fileSelected$.getValue().$id,
					{ description }
				);
			}
		});

		// Needed to make revalidation happen on each character change
		this.errorStateMatcher = new (class extends ErrorStateMatcher {
			public isErrorState(
				control: UntypedFormControl | null,
				form: FormGroupDirective | NgForm | null
			): boolean {
				return control.invalid;
			}
		})();

		this.attachedFilesService.editable$
			.pipe(takeUntil(this.destroy$))
			.subscribe((editable) => (editable ? this.fileDescription.enable() : this.fileDescription.disable()));
	}

	public ngAfterViewInit(): void {
		this.attachedFilesService.scrollToSelectedFile(this.attachedFilesService.fileSelected$.getValue());
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public selectImageToPreview(file: Attachment) {
		this.attachedFilesService.selectFile(file);
		analyticsEventsService.fileViewed(this.attachedFilesService.planId, file.view.type);
	}

	public saveFileDescription() {
		this.attachedFilesService.api.persistAttachment(this.attachedFilesService.fileSelected$.getValue().$id);
	}
}
